// Core type system for the Design Decision Engine.
// See docs/SPEC.md sections 3, 4, 9.

export type Objective = "rental" | "personal" | "flip";

export type NodeType = "constraint" | "decision" | "product" | "derived";
export type EdgeType =
  | "constrains"
  | "harmonizes"
  | "conflicts"
  | "depends_on"
  | "anchors"
  | "propagates_to";

export type HierarchyLevel = 0 | 1 | 2 | 3 | 4 | 5;
// 0 constraints, 1 anchors, 2 primary, 3 secondary, 4 accents, 5 personality

export type ImpactBadge = "foundational" | "important" | "minor";

export interface GraphNode {
  id: string;
  nodeType: NodeType;
  level: HierarchyLevel;
  domain: string; // paint, flooring, hardware, lighting...
  label: string;
  status: "open" | "resolved" | "deferred";
  payload: Record<string, unknown>;
  confidence?: number;
  impactBadge?: ImpactBadge;
}

export interface GraphEdge {
  from: string;
  to: string;
  edgeType: EdgeType;
  weight?: number;
  ruleId?: string; // set when a conflict edge is created by a rule
}

export interface DesignGraph {
  roomId: string;
  nodes: Map<string, GraphNode>;
  edges: GraphEdge[];
}

// ---------- Design DNA ----------

export interface Trait {
  value: number; // 0-100
  latitude: number; // 0-100: how little the user cares (high = defer to objective)
}

// A named aesthetic the user is going for. Steers recommendations but never
// overrides design principles (see stylePull).
export type StyleKey =
  | "scandi" | "warm-minimal" | "modern-farmhouse"
  | "moody" | "coastal" | "contemporary" | "transitional";

// Which surface a style target or insight applies to. Scandi wants a
// near-white wall but a light, not white, floor, so targets differ by
// surface.
export type Surface = "wall" | "floor";

// Voice of the suggestions. Facts stay the same; the delivery changes.
// "playful" is a productive roast: it only gets spicy when a pick genuinely
// clashes, and it always hands back a fix.
export type Tone = "professional" | "playful" | "coach";

// How hard the declared vibe is allowed to steer a pick, 0..1. The default
// keeps design principles in charge roughly three-quarters of the time;
// the vibe breaks ties and nudges, it does not run the show.
export const DEFAULT_STYLE_PULL = 0.25;

export interface DesignDNA {
  traits: {
    warmth: Trait;
    minimalism: Trait;
    contrast: Trait;
    naturalMaterials: Trait;
    tradContemporary: Trait; // 0 traditional .. 100 contemporary
    luxuryPractical: Trait; // 0 luxury .. 100 practical
  };
  flags: string[]; // rule-exception flags: intentional_mixed_metals, etc.
  styleVibe?: StyleKey; // declared aesthetic, if any
  stylePull?: number; // 0..1 steer strength; defaults to DEFAULT_STYLE_PULL
  tone?: Tone; // suggestion voice; defaults to "professional"
}

export interface WeightProfile {
  durability: number;
  maintenance: number;
  costEfficiency: number;
  broadAppeal: number;
  personalStyle: number;
  joy: number;
  resale: number;
  photography: number;
  trendAlignment: number;
}

export const PRESETS: Record<Objective, WeightProfile> = {
  rental: {
    durability: 100, maintenance: 95, costEfficiency: 90, broadAppeal: 90,
    personalStyle: 20, joy: 30, resale: 60, photography: 40, trendAlignment: 15,
  },
  personal: {
    durability: 70, maintenance: 70, costEfficiency: 50, broadAppeal: 30,
    personalStyle: 100, joy: 95, resale: 30, photography: 25, trendAlignment: 15,
  },
  flip: {
    durability: 60, maintenance: 50, costEfficiency: 80, broadAppeal: 100,
    personalStyle: 15, joy: 20, resale: 100, photography: 95, trendAlignment: 90,
  },
};

// ---------- Room context ----------

export interface LightProfile {
  orientation: "north" | "south" | "east" | "west" | "mixed";
  windowRatio: number; // window area / floor area, rough
  bulbCCT: number; // 2700 warm .. 5000 cool
}

export type RoomType =
  | "kitchen" | "bath" | "bedroom" | "living" | "dining"
  | "laundry" | "office" | "other";

export type Orientation = LightProfile["orientation"];
export type DaylightLevel = "low" | "medium" | "high";

export interface RoomContext {
  roomId: string;
  roomType: RoomType;
  sqft: number;
  wallAreaSqft: number;
  ceilingHeightFt: number;
  light: LightProfile;
  objective: Objective;
}

// ---------- Candidates and scoring ----------

export interface Candidate {
  productRef: string; // catalog key
  label: string;
  attributes: Record<string, unknown>;
  criterionScores: Partial<Record<keyof WeightProfile, number>>; // 0-1 each
}

export interface ScoredCandidate extends Candidate {
  score: number; // weighted composite 0-1
  ruleHits: RuleHit[];
  hardFiltered: boolean;
}

export interface RuleHit {
  ruleId: string;
  name: string;
  weight: number;
  satisfied: boolean;
  suppressedByException?: string;
  message: string;
}

export interface Recommendation {
  nodeId: string;
  ranked: ScoredCandidate[];
  confidence: number; // 0-100
  confidenceParts: {
    ruleAgreement: number;
    margin: number;
    profileCertainty: number;
    dataCoverage: number;
  };
  badge: ImpactBadge;
  reasoning: string[]; // plain-language trace lines
  tradeoffs: string[];
}

// ---------- Domain engine contract ----------

export interface DomainEngine {
  id: string;
  version: string;
  levels: HierarchyLevel[];
  enumerateDecisions(graph: DesignGraph, room: RoomContext): GraphNode[];
  generateCandidates(
    node: GraphNode,
    graph: DesignGraph,
    room: RoomContext
  ): Candidate[];
}
